import { useMemo, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from 'cpool-components/general/Popover';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft, LuChevronRight, LuPlus, LuX } from 'react-icons/lu';

import type { MonthKey, MonthRange } from './dateCategoryUtils';
import { applyMonthClick, fromMonthKey, mergeMonthsToRanges, monthKeysInRange, toMonthKey } from './dateCategoryUtils';
import { cn } from '~/utils/cnTailwind';

const MONTH_INDEXES = Array.from({ length: 12 }, (_, month) => month);

type Props = {
    selectedMonths: MonthKey[];
    onChange: (months: MonthKey[]) => void;
    disabled?: boolean;
};

// Own grid instead of a date-picker lib: needs per-month clicks for the reducer
export const MonthRangePicker: React.FC<Props> = ({ disabled, onChange, selectedMonths }) => {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    // The first click of a not-yet-completed range; the next click fills the span up to it.
    const [anchor, setAnchor] = useState<MonthKey | null>(null);
    const [displayedYear, setDisplayedYear] = useState(() =>
        selectedMonths.length > 0 ? fromMonthKey(selectedMonths[0]).year : new Date().getFullYear(),
    );

    const selected = useMemo(() => new Set(selectedMonths), [selectedMonths]);
    const overviewRanges = useMemo(() => mergeMonthsToRanges(selectedMonths), [selectedMonths]);
    const rangeEnds = useMemo(
        () => new Set(overviewRanges.flatMap(({ end, start }) => [start, end])),
        [overviewRanges],
    );

    const monthName = useMemo(() => new Intl.DateTimeFormat([i18n.language, 'de'], { month: 'long' }), [i18n.language]);
    const monthAndYear = useMemo(
        () => new Intl.DateTimeFormat([i18n.language, 'de'], { month: 'long', year: 'numeric' }),
        [i18n.language],
    );
    const formatMonthKey = (key: MonthKey) => {
        const { month, year } = fromMonthKey(key);
        return monthAndYear.format(new Date(year, month, 1));
    };
    // A lone month reads as one label; a span joins its ends with an en dash.
    const rangeLabel = ({ end, start }: MonthRange) =>
        start === end ? formatMonthKey(start) : `${formatMonthKey(start)} – ${formatMonthKey(end)}`;

    const close = () => {
        setOpen(false);
        setAnchor(null);
    };

    const selectMonth = (monthIndex: number) => {
        const { anchor: nextAnchor, months } = applyMonthClick(
            toMonthKey(displayedYear, monthIndex),
            selectedMonths,
            anchor,
        );
        setAnchor(nextAnchor);
        onChange(months);
    };

    const removeRange = (range: MonthRange) => {
        const removed = new Set(monthKeysInRange(range));
        setAnchor(null);
        onChange(selectedMonths.filter((key) => !removed.has(key)));
    };

    const navButton = 'rounded-md border border-grey-outline-light p-1.5 text-midnight hover:bg-surface-light';

    return (
        <Popover
            open={open && !disabled}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setAnchor(null);
            }}
        >
            <PopoverTrigger asChild>
                <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuPlus className="h-4 w-4" />} disabled={disabled}>
                    {t('DOCUMENT_DEMANDER.ADD_DATE')}
                </Button>
            </PopoverTrigger>

            <PopoverContent align="start" sideOffset={8} className="w-max border-grey-stroke p-0 shadow-1">
                <div className="flex items-center justify-between gap-4 border-b border-grey-stroke px-4 py-3">
                    <span className="text-sm font-medium text-midnight">{t('DOCUMENT_DEMANDER.DATE_SELECT')}</span>
                    <button
                        type="button"
                        aria-label={t('BUTTONS.CANCEL')}
                        className="rounded p-0.5 text-midnight-placeholder hover:text-midnight"
                        onClick={close}
                    >
                        <LuX className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex">
                    <div className="flex flex-col gap-3 p-4">
                        <div className="flex items-center justify-between gap-4">
                            <button
                                type="button"
                                aria-label={String(displayedYear - 1)}
                                className={navButton}
                                onClick={() => setDisplayedYear((year) => year - 1)}
                            >
                                <LuChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-sm font-medium text-midnight">{displayedYear}</span>
                            <button
                                type="button"
                                aria-label={String(displayedYear + 1)}
                                className={navButton}
                                onClick={() => setDisplayedYear((year) => year + 1)}
                            >
                                <LuChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-3 gap-y-2">
                            {MONTH_INDEXES.map((monthIndex) => {
                                const monthKey = toMonthKey(displayedYear, monthIndex);
                                const isRangeEnd = rangeEnds.has(monthKey);
                                const isInRange = selected.has(monthKey) && !isRangeEnd;
                                return (
                                    <button
                                        key={monthIndex}
                                        type="button"
                                        data-testid={`month-cell-${monthIndex}`}
                                        className={cn(
                                            'border px-3 py-1.5 text-center text-sm whitespace-nowrap',
                                            isRangeEnd && 'rounded-md border-midnight bg-midnight text-white',
                                            isInRange && 'border-transparent bg-surface-light text-midnight',
                                            !isRangeEnd &&
                                                !isInRange &&
                                                'rounded-md border-transparent text-midnight hover:bg-surface-light',
                                        )}
                                        onClick={() => selectMonth(monthIndex)}
                                    >
                                        {monthName.format(new Date(displayedYear, monthIndex, 1))}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 border-l border-grey-stroke p-4">
                        <span className="text-sm font-medium text-midnight">
                            {t('DOCUMENT_DEMANDER.DATE_OVERVIEW')}
                        </span>
                        <ul className="flex list-none flex-col gap-2 p-0">
                            {overviewRanges.map((range) => (
                                <li
                                    key={range.start}
                                    className="flex items-center justify-between gap-2 rounded-full border border-grey-outline-light bg-surface-light py-1 pr-2 pl-3 text-sm whitespace-nowrap text-midnight"
                                >
                                    <span>{rangeLabel(range)}</span>
                                    <button
                                        type="button"
                                        aria-label={t('BUTTONS.DELETE')}
                                        className="shrink-0 text-midnight-placeholder hover:text-midnight"
                                        onClick={() => removeRange(range)}
                                    >
                                        <LuX className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
